/**
 * Shared handling for the PAM backend's permission-denied contract.
 *
 * Every gated endpoint (``@require_permission`` in
 * ``pam/servers/utils/auth.py``) returns a stable 403 body when the caller
 * is authenticated but lacks the required permission:
 *
 *   {
 *     "error": "permission_denied",
 *     "message": "<user-facing text>",
 *     "suggested_roles": ["recon_editor", ...]   // role(s) that grant access
 *   }
 *
 * The body names the role(s) to request (role names are not secret — the
 * Permissions app serves /roles to any authenticated caller) but never the
 * raw required-permission ids or the caller's held set. Apps should use
 * `isPermissionDenied` in their catch blocks and render
 * `getPermissionDeniedMessage`, which names the role and links to the
 * Permissions app, instead of a generic failure toast.
 *
 * The backend also returns two sibling 403s that are *terminal* — the caller
 * can't be granted anything, so neither carries ``suggested_roles`` and neither
 * renders a "Request access" affordance:
 *
 *   { "error": "outside_org",      "message": "<user-facing text>" }
 *   { "error": "account_inactive",  "message": "<user-facing text>" }
 *
 * ``outside_org`` is an email off the allowed domains; ``account_inactive`` is an
 * in-org account an admin has switched off. Both are informational — the remedy
 * is an administrator, not a request — but they are distinct because only the
 * second one can be turned back on.
 *
 * Preferred usage — one call handles both shapes and routes anything else
 * back to the caller's generic handling:
 *
 *   import { notifyPermissionError } from '@1823-partners/core';
 *
 *   } catch (err) {
 *     if (!notifyPermissionError(showNotification, err)) {
 *       showNotification('Failed to save', 'error');
 *     }
 *   }
 *
 * For a permission denial, ``notifyPermissionError`` shows a warning toast
 * with a "Request access" button. For an outside-org error it shows a plain
 * error toast with no button.
 *
 * What that button does depends on whether the app mounted
 * ``PermissionRequestProvider``:
 *
 *   - **mounted** — it opens ``RequestAccessDialog`` and submits the request
 *     to the app's own ``POST /request-role`` on confirmation, so the user
 *     never leaves the page they were on.
 *   - **not mounted** — it deep-links into the Permissions app's Request Role
 *     tab (pre-selecting the suggested role when there's exactly one), which
 *     is the pre-1.29 behavior.
 *
 * Call sites don't choose: ``getPermissionDeniedAction`` picks based on
 * whether a handler is registered, so an app upgrades by mounting the
 * provider and changing nothing else.
 */

/** Stable error code emitted by the backend on a failed permission gate. */
export const PERMISSION_DENIED_ERROR = 'permission_denied';

// The Permissions app, per environment. A denied user must land on the
// Permissions deployment that matches the app they were using: a prod app
// links to the prod Permissions app; uat/dev apps link to the uat (preview)
// one — otherwise a role requested in uat wouldn't show up where the prod
// admins look (and vice versa). These mirror the hosts the landing page uses.
export const PERMISSIONS_APP_URL_PROD = 'https://permissions.1823.partners';
export const PERMISSIONS_APP_URL_UAT =
  'https://ashy-mushroom-045a3fa0f-preview.eastus2.7.azurestaticapps.net';

/**
 * Back-compat alias for the prod Permissions app URL. New code should call
 * :func:`getPermissionsAppUrl` so the link follows the current environment;
 * this constant stays the prod address for existing direct importers.
 */
export const PERMISSIONS_APP_URL = PERMISSIONS_APP_URL_PROD;

/**
 * The Permissions app base URL for the *current* environment.
 *
 * Driven by ``REACT_APP_DB_ENV`` — the same signal the API transport uses —
 * which app builds inline: ``prod`` for a production release, ``uat`` for the
 * main→preview build, ``dev`` for local. Explicit ``uat``/``dev`` map to the
 * uat (preview) deployment; ``prod`` maps to production.
 *
 * When ``REACT_APP_DB_ENV`` is absent (some prod proxy apps don't set a
 * frontend DB-env var) we fall back on ``NODE_ENV``: a deployed build
 * (``NODE_ENV === 'production'``) defaults to **prod** so a prod bundle
 * without the var never deep-links real users into the uat app; only a
 * local/test build (``NODE_ENV !== 'production'``) falls back to uat.
 *
 * ``process.env.*`` is referenced directly so CRA's DefinePlugin can replace
 * each with a string literal at build time (bare ``process.env`` isn't
 * polyfilled in the browser); the guards keep it safe under jest/SSR where
 * ``process`` is a real object or absent.
 */
export const getPermissionsAppUrl = () => {
  let dbEnv;
  try {
    dbEnv = process.env.REACT_APP_DB_ENV;
  } catch (e) {
    dbEnv = undefined;
  }
  if (dbEnv === 'uat' || dbEnv === 'dev') return PERMISSIONS_APP_URL_UAT;
  if (dbEnv === 'prod') return PERMISSIONS_APP_URL_PROD;
  // No explicit DB env: a deployed bundle defaults to prod (never silently
  // route prod users to uat); local/test falls back to uat.
  let nodeEnv;
  try {
    nodeEnv = process.env.NODE_ENV;
  } catch (e) {
    nodeEnv = undefined;
  }
  return nodeEnv === 'production' ? PERMISSIONS_APP_URL_PROD : PERMISSIONS_APP_URL_UAT;
};

/**
 * Client-side fallback mirroring the backend's message, used when a
 * permission_denied response arrives without a message body.
 */
export const DEFAULT_PERMISSION_DENIED_MESSAGE =
  "You don't have access to perform this action. " +
  'To get access, request the required role for this app in the ' +
  'Permissions app (or ask a permissions administrator).';

/**
 * True iff *error* is an axios-style error for a 403 carrying the
 * permission_denied contract. Safe on network errors / non-axios values.
 */
export const isPermissionDenied = (error) =>
  error?.response?.status === 403 &&
  error?.response?.data?.error === PERMISSION_DENIED_ERROR;

/**
 * The role id(s) the backend says would grant access, or [] when the error
 * isn't a permission denial or the body carries no suggestions.
 */
export const getSuggestedRoles = (error) => {
  if (!isPermissionDenied(error)) return [];
  const roles = error.response.data?.suggested_roles;
  return Array.isArray(roles) ? roles : [];
};

/**
 * The user-facing message for a permission-denied error, or null when the
 * error is not a permission denial (so callers can fall through to their
 * generic error handling). Names the role(s) to request and links to the
 * Permissions app so the user knows exactly where to go and what to ask for.
 */
export const getPermissionDeniedMessage = (error) => {
  if (!isPermissionDenied(error)) return null;
  const roles = getSuggestedRoles(error);
  const appUrl = getPermissionsAppUrl();
  const lead = "You don't have access to perform this action.";
  if (roles.length === 1) {
    return `${lead} Request the "${roles[0]}" role in the Permissions app: ${appUrl}`;
  }
  if (roles.length > 1) {
    return (
      `${lead} Request one of these roles in the Permissions app ` +
      `(${appUrl}): ${roles.join(', ')}`
    );
  }
  // No role hint from the server — fall back to the backend message (or our
  // default), still pointing the user at the Permissions app.
  const base = error.response.data?.message || DEFAULT_PERMISSION_DENIED_MESSAGE;
  return `${base} ${appUrl}`;
};

/** Stable error code the backend emits when the caller's email is off-org. */
export const OUTSIDE_ORG_ERROR = 'outside_org';

/**
 * Client-side fallback mirroring the backend's outside-org message, used
 * when an outside_org response arrives without a message body.
 */
export const DEFAULT_OUTSIDE_ORG_MESSAGE =
  "Your account isn't part of the 1823 Partners organization, so it can't " +
  'be granted access to this app. If you believe this is a mistake, contact ' +
  'a permissions administrator.';

/**
 * True iff *error* is an axios-style 403 carrying the outside_org contract.
 * Safe on network errors / non-axios values.
 */
export const isOutsideOrg = (error) =>
  error?.response?.status === 403 &&
  error?.response?.data?.error === OUTSIDE_ORG_ERROR;

/**
 * The user-facing message for an outside-org error, or null when the error
 * is not one (so callers can fall through to generic handling). There is no
 * role to request, so this is purely informational.
 */
export const getOutsideOrgMessage = (error) => {
  if (!isOutsideOrg(error)) return null;
  return error.response.data?.message || DEFAULT_OUTSIDE_ORG_MESSAGE;
};

/** Stable error code the backend emits when the caller's account is switched off. */
export const ACCOUNT_INACTIVE_ERROR = 'account_inactive';

/**
 * Client-side fallback mirroring the backend's message, used when an
 * account_inactive response arrives without a message body.
 */
export const DEFAULT_ACCOUNT_INACTIVE_MESSAGE =
  "Your account has been deactivated, so it can't be granted access. Contact " +
  'a permissions administrator if you believe this is a mistake.';

/**
 * True iff *error* is an axios-style 403 carrying the account_inactive
 * contract. Safe on network errors / non-axios values.
 *
 * The permission gate returns this instead of ``permission_denied`` for an
 * in-org caller whose ``permissions_users`` row is DELETED. Such a caller holds
 * no permissions at all and no role request can change that until an admin
 * reactivates the account, so — like ``outside_org`` — it is terminal and must
 * not render a "Request access" affordance.
 */
export const isAccountInactive = (error) =>
  error?.response?.status === 403 &&
  error?.response?.data?.error === ACCOUNT_INACTIVE_ERROR;

/**
 * The user-facing message for a deactivated account, or null when the error is
 * not one. Purely informational: the remedy is an administrator, not a request.
 */
export const getAccountInactiveMessage = (error) => {
  if (!isAccountInactive(error)) return null;
  return error.response.data?.message || DEFAULT_ACCOUNT_INACTIVE_MESSAGE;
};

/**
 * Deep link into the Permissions app's Request Role tab. The presence of the
 * ``requestRole`` query param tells the app to open that tab; a non-empty
 * value pre-selects the role so the user only has to add a reason and submit.
 * Pass a falsy roleId (e.g. when several roles could grant access) to just
 * open the tab without pre-selecting. The base URL follows the current
 * environment (see :func:`getPermissionsAppUrl`) so a uat/dev app links to
 * the uat Permissions app and a prod app to the prod one.
 */
export const buildRequestRoleUrl = (roleId) =>
  `${getPermissionsAppUrl()}/?requestRole=${encodeURIComponent(roleId || '')}`;

/**
 * The user-facing text for a permission denial, phrased for a toast that
 * carries a "Request access" *button* (so, unlike getPermissionDeniedMessage,
 * it does NOT inline the raw Permissions-app URL). Returns null when the
 * error isn't a permission denial.
 */
export const getPermissionDeniedText = (error) => {
  if (!isPermissionDenied(error)) return null;
  const roles = getSuggestedRoles(error);
  const lead = "You don't have access to perform this action.";
  if (roles.length === 1) {
    return `${lead} Request the "${roles[0]}" role to get access.`;
  }
  if (roles.length > 1) {
    return `${lead} Request one of these roles: ${roles.join(', ')}.`;
  }
  return error.response.data?.message || DEFAULT_PERMISSION_DENIED_MESSAGE;
};

/**
 * Default justification the request dialog pre-fills, so a user who just
 * wants access doesn't have to compose one. Mirrors the fallback the
 * backend applies when ``reason`` arrives blank (``request_role_blueprint``
 * in pam-master), so the admin queue reads the same either way.
 */
export const buildDefaultRequestReason = (appName) =>
  appName
    ? `Requested from the ${appName} app after a permission denial.`
    : 'Requested after a permission denial.';

/**
 * POST the role request to the *current app's* backend.
 *
 * Every PAM server registers the shared ``/request-role`` route
 * (``pam.servers.request_role_blueprint``), so this goes through the app's
 * own transport — no cross-app call, no Permissions-app credentials in the
 * caller's SWA proxy. Identity is taken server-side from the SWA principal;
 * we never send an email.
 *
 * Resolves to the response body: ``{ok: true}`` for a fresh request,
 * ``{ok: true, already_pending: true}`` when the caller had already requested
 * this role and it is still awaiting review, or ``{ok: true,
 * already_granted: true}`` when they already hold it (the server refuses to
 * write there, since a PENDING row over an ACTIVE grant would revoke it).
 * Rejects on transport / validation failures so the dialog can surface them.
 *
 * @param {Object}  params
 * @param {Object}  params.api      App transport from ``createAppTransport``.
 * @param {string}  params.roleId   Role to request.
 * @param {string}  [params.reason] Justification; blank → server default.
 * @param {string}  [params.appName] App slug, used in the default reason.
 */
export const submitRoleRequest = async ({ api, roleId, reason, appName }) => {
  const response = await api.post('/request-role', {
    role_id: roleId,
    reason: reason || buildDefaultRequestReason(appName),
    app: appName,
  });
  return response?.data ?? response;
};

// Handler installed by ``PermissionRequestProvider`` when an app mounts it.
// Registered in a slot rather than threaded through every call site: the
// ``notifyPermissionError`` contract is already used from ~30 catch blocks per
// app, and each app is a single SPA with exactly one provider. With no
// provider mounted the slot stays null and the button falls back to the deep
// link, so apps adopt the in-place flow purely by mounting the provider.
//
// The slot lives on ``globalThis``, NOT in a module-level ``let``. Rollup
// builds each package entrypoint (``.``, ``/contexts``, ``/components``,
// ``/utils``) as an independent bundle with no shared chunks, so this module
// is duplicated into every one of them. A module-level variable would give
// each bundle its own registry: a provider imported from
// ``@1823-partners/core/contexts`` would be invisible to
// ``notifyPermissionError`` imported from ``@1823-partners/core``, silently
// falling back to the external link. Worse, rollup tree-shakes the writes to
// such a variable out of any bundle that never reads it — the built
// ``dist/contexts`` bundle really did end up with an empty
// ``clearRequestAccessHandler`` and no ``registerRequestAccessHandler`` at
// all. A property on the shared global survives both problems.
const HANDLER_SLOT = '__1823PartnersCoreRequestAccessHandler__';

// Falls back to a module-local object only where no global exists (exotic SSR
// / sandboxed evaluation); that degrades to the old per-bundle behavior rather
// than throwing.
const localFallback = {};
const slotHost = () => (typeof globalThis !== 'undefined' ? globalThis : localFallback);

/**
 * Install the in-app "request access" handler. Called by
 * :func:`PermissionRequestProvider` on mount; *fn* receives the suggested
 * role ids for the denial that produced the button.
 */
export const registerRequestAccessHandler = (fn) => {
  slotHost()[HANDLER_SLOT] = typeof fn === 'function' ? fn : null;
};

/**
 * Uninstall the handler (provider unmount). Safe to call when none is set.
 */
export const clearRequestAccessHandler = () => {
  slotHost()[HANDLER_SLOT] = null;
};

/** The registered handler, or null. */
const getRequestAccessHandler = () => slotHost()[HANDLER_SLOT] || null;

/** True when an app has mounted a provider that can submit requests in place. */
export const hasRequestAccessHandler = () => getRequestAccessHandler() !== null;

/**
 * The notification *action* (button) for a permission denial, or null when the
 * error is not a permission denial (callers render no button).
 *
 * Always returns **both** ``href`` and ``onClick``, and the choice between
 * them happens at *click* time:
 *
 *   - ``href`` is the deep link into the Permissions app's Request Role tab,
 *     pre-selecting the suggested role when the backend named exactly one.
 *     Renderers should keep it on a real anchor so middle-click / copy-link
 *     work and an app with no provider behaves exactly as it did pre-1.29.
 *   - ``onClick`` looks up the registered handler *when the user clicks*. With
 *     a provider mounted it calls ``preventDefault()`` and opens the in-app
 *     dialog; with none it does nothing and lets the anchor follow ``href``.
 *
 * Deciding at click time rather than when this object is built is deliberate:
 * ``PermissionRequestProvider`` registers in a passive effect, which React runs
 * *after* its children have rendered. A denial rendered in the provider's first
 * commit would otherwise capture "no handler" and stay an external link for the
 * lifetime of that element.
 */
export const getPermissionDeniedAction = (error) => {
  if (!isPermissionDenied(error)) return null;
  const roles = getSuggestedRoles(error);
  const roleId = roles.length === 1 ? roles[0] : null;
  return {
    label: 'Request access',
    href: buildRequestRoleUrl(roleId),
    onClick: (event) => {
      const handler = getRequestAccessHandler();
      // No provider (or one that unmounted since render): fall through and let
      // the anchor navigate to the Permissions app.
      if (!handler) return;
      event?.preventDefault?.();
      handler(roles);
    },
  };
};

/**
 * One-stop handler for the backend's two authz error shapes. Pass your
 * notification context's ``showNotification`` and the caught error:
 *
 *   - permission_denied → warning toast + "Request access" button (deep-links
 *     to the Permissions app, pre-selecting the suggested role when unique).
 *   - outside_org       → plain error toast, no button (nothing to request).
 *   - anything else     → does nothing.
 *
 * Returns true iff it handled the error (so the caller can fall through to
 * its own generic failure toast only when this returns false). The optional
 * third ``options`` argument to ``showNotification`` carries the action; the
 * shared NotificationContext renders it as a button (older notification
 * providers that ignore the third arg simply show the toast without one).
 */
export const notifyPermissionError = (showNotification, error) => {
  if (isOutsideOrg(error)) {
    showNotification(getOutsideOrgMessage(error), 'error');
    return true;
  }
  // Terminal like outside_org: the account exists but is switched off, so no
  // role request can help and no button is offered. Checked before
  // permission_denied because the gate returns one or the other, never both.
  if (isAccountInactive(error)) {
    showNotification(getAccountInactiveMessage(error), 'error');
    return true;
  }
  if (isPermissionDenied(error)) {
    showNotification(getPermissionDeniedText(error), 'warning', {
      action: getPermissionDeniedAction(error),
    });
    return true;
  }
  return false;
};
